import parser from 'html-react-parser'
import Slider from 'react-slick'
import styled, { useTheme } from 'styled-components'
import { Typo, TypoVariant } from '@/components/common/typography'
import { FitpetMallEventEnum } from '@/constants/fitpet-mall-event.enum'
import { useTracker } from '@/containers/contexts/EventTrackingProvider'
import { useCustomRouter } from '@/containers/hooks'
import useReactSlickBanner from '@/containers/hooks/useReactSlickBanner'
import FitpetPlusLogoSimpleSvg from '@/images/fitpet_logo_mono_color.svg'
import { ProductDetailTextBanner } from '@/types/banner.type'

type Banner = ProductDetailTextBanner
interface TextBannerSliderProps {
  banners?: Banner[]
  eventTrackerName: {
    view: FitpetMallEventEnum
    click: FitpetMallEventEnum
  }
  isLoading: boolean
  isError: boolean
}

const TextBannerSlider = ({ banners, eventTrackerName, isError }: TextBannerSliderProps) => {
  const { push } = useCustomRouter()
  const { triggerCustomEvent } = useTracker()
  const { elementRef, currentBannerIndex, settings } = useReactSlickBanner<Banner>(eventTrackerName.view, banners)
  const { color } = useTheme()

  const handleClick = (banner: Banner) => {
    push(banner.linkUrl)
    triggerCustomEvent(eventTrackerName.click, {
      ...banner.trackerProperties,
    })
  }

  const pagination = banners && {
    isShow: banners.length > 1,
    currentPage: `${currentBannerIndex + 1}/${banners.length}`,
  }

  if (!isError && !banners?.length) {
    return null
  }

  return (
    <StyledContainer ref={elementRef}>
      {isError ? (
        <StyledErrorContainer>
          <StyledFitpetPlusLogoSimpleSvg />
        </StyledErrorContainer>
      ) : (
        <Slider {...settings}>
          {banners?.map((banner) => (
            <StyledSliderItem key={banner.id} onClick={() => handleClick(banner)}>
              <Typo variant={TypoVariant.Body3Regular} color={color.gray700}>
                {parser(banner.bannerText)}
              </Typo>
            </StyledSliderItem>
          ))}
        </Slider>
      )}
      {pagination?.isShow && <StyledPagination>{pagination.currentPage}</StyledPagination>}
    </StyledContainer>
  )
}

const StyledErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 4.8rem;
`
const StyledFitpetPlusLogoSimpleSvg = styled(FitpetPlusLogoSimpleSvg)`
  width: 4.8rem;
  color: ${(props) => props.theme.color.gray100};
`

const StyledContainer = styled.div`
  position: relative;
  cursor: pointer;
  border-radius: 0.8rem;
  background-color: ${(props) => props.theme.color.blue30};

  .slick-list {
    width: 100%;
  }

  .slick-slider {
    display: flex;
    align-items: center;
    padding: 1.4rem 1.6rem;
    height: 4.8rem;
  }
`

const StyledPagination = styled.div`
  position: absolute;
  top: 50%;
  right: 0.8rem;
  transform: translateY(-50%);

  padding: 0.3rem 0.6rem;
  border-radius: 2rem;
  font-size: 1rem;
  line-height: 1.2rem;
  letter-spacing: 0.1em;
  color: ${(props) => props.theme.color.grayWhite};
  background-color: rgba(0, 0, 0, 0.4);
`

const StyledSliderItem = styled.div`
  width: 100%;
  height: 100%;
`

const StyledBannerText = styled.div`
  height: 100%;
  font-size: 1.4rem;
  letter-spacing: -0.02em;
  line-height: 2rem;
  color: ${(props) => props.theme.color.gray700};

  span.text-emphasis {
    color: ${(props) => props.theme.color.blue500};
  }
`

export default TextBannerSlider
